/* eslint-disable max-lines */
import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import flowRight from 'lodash/flowRight'

import { ErrorView, Loading } from '@fielded/shared-ui'
import { toast } from '@fielded/shared-ui/src/components/Page/Page'

import { withUser } from '../../../common/AuthenticationProvider'
import { findShipments, selectShipments } from '../../../root/reducers/shipments'
import withConfig from '../../../van-shared/hoc/withConfig'
import { hasFeature } from '../../../van-shared/utils/features'
import { withApi } from '../../../common/ApiProvider'
import { useOnlineOffline } from '../../../sync/common'
import { SHIPMENT_STATUS } from '@fielded/fs-api/lib/shipment/constants'

import {
  isPsmResellerWarehouseUser,
  isPsmWarehouseUser,
  isPsmDriverUser
} from '../common/utils'

import OfflineWarning from '../common/OfflineWarning'
import DriverDeliveries from './PSMDeliveries'
import useShipments from './psm-shipments/hooks/useShipments'

const PSMDeliveriesContainer = ({
  api,
  user,
  config,
  history,
  showCompleted
}) => {
  const dispatch = useDispatch()

  const options = {
    noExternalShipments: true,
    // Use PG-backed shipments list when enabled to ensure data appears for drivers
    shouldUsePGShipments: hasFeature(config.features, 'features.usePGShipments')
  }
  const shipmentType = showCompleted ? 'complete' : 'incomplete'

  const {error, locationName, shipments, loading} = useShipments({
    api, user, config, key: shipmentType, filters: shipment => shipment.planningType !== 'interwarehouse', options
  })

  const shipmentsFromStore = useSelector(selectShipments)
  const [fetchingOnlineShipments, setFetchingOnlineShipments] = useState(false)
  const [programs, setPrograms] = useState([])

  const [loadingOnlineShipments, setLoadingOnlineShipments] = useState(false)
  const [packingInProgress, setPackingInProgress] = useState(false)
  const [offlineError, setOfflineError] = useState(false)
  const [selectedDeliveriesIds, setSelectedDeliveriesIds] = useState([])
  const [showModal, setShowModal] = useState(false)

  const withListInGeoLocation = hasFeature(config.features, 'shipments:listInGeoLocation')

  const useRouteSpecificFilter = true

  const userLocation = user.location

  const initialize = async () => {
    // Drivers are not tied to a particular set of programs for now
    // so we need the unfiltered program list.
    // This means all programs, though, including the EPN ones.
    let programs = await api.program.list({ unfiltered: true })
    setPrograms(programs)
  }

  const onPackDelivery = async () => {
    setPackingInProgress(true)
    const selectedDeliveries = selectedDeliveriesIds.map((id) => shipmentsFromStore[id])
    const payload = {
      shipments: selectedDeliveries
    }

    // Advance selected shipments
    try {
      const updatedSnapshots = await api.shipment.bulkAdvanceSnapshots(payload)
      if (updatedSnapshots && updatedSnapshots.length) {
        const userLocation = user.location
        const withListInGeoLocation = hasFeature(config.features, 'shipments:listInGeoLocation')
        const useRouteSpecificFilter = true
        const options = { noExternalShipments: true }

        // Update store
        await dispatch(
          findShipments(
            userLocation,
            withListInGeoLocation,
            useRouteSpecificFilter,
            options
          )
        )
      }
      setPackingInProgress(false)
      setShowModal(false)
      toast({
        title: 'SHIPMENT STATUS UPDATED.',
        message: 'The selected shipments have been changed to Packed status.',
        type: 'success'
      })
      window.location.reload()
    } catch (e) {
      throw new Error(`Error found: ${e}`)
    }
  }

  const closeModal = () => {
    setShowModal(false)
  }

  const onToggleCheckbox = (event, delivery = null) => {
    let isChecked = event
    let newSelectedDeliveriesIds = []

    if (event.target) {
      isChecked = event.target.checked
    }

    // this handles selecting all deliveries
    if (!delivery) {
      if (isChecked) {
        // Use selectableIds if provided (from PSMDriverDeliveryCardContainer),
        // otherwise fall back to packedDeliveriesIds/newDeliveriesIds
        if (event.selectableIds) {
          newSelectedDeliveriesIds = event.selectableIds
        } else {
          newSelectedDeliveriesIds = isWarehouseUser
            ? newDeliveriesIds
            : packedDeliveriesIds
        }
      } else {
        newSelectedDeliveriesIds = []
      }
    }

    if (delivery) {
      if (isChecked) {
        newSelectedDeliveriesIds = [...selectedDeliveriesIds, delivery.snapshotId]
      } else {
        newSelectedDeliveriesIds = selectedDeliveriesIds.filter(snapshotId => snapshotId !== delivery.snapshotId)
      }
    }

    setSelectedDeliveriesIds(newSelectedDeliveriesIds)
  }

  const loadOnlineShipments = async () => {
    const options = { online: true }
    setFetchingOnlineShipments(true)
    try {
      await dispatch(
        findShipments(
          userLocation,
          withListInGeoLocation,
          useRouteSpecificFilter,
          options
        )
      )

      setLoadingOnlineShipments(true)
      setOfflineError(false)
    } catch (error) {
      setOfflineError(true)
    } finally {
      // We have previously loaded online shipments on the reducer already
      setFetchingOnlineShipments(false)
    }
  }

  useEffect(() => {
    initialize()
  }, [])

  if (error) {
    return <ErrorView title='Shipment' error={error} />
  }

  // Ask again if the hasReceivedShipment check here was a mistake.
  if (loading || fetchingOnlineShipments) {
    return <Loading loadingText={`Loading ${showCompleted ? 'completed' : 'scheduled'} shipments …`} />
  }

  let onLoadOnlineShipments = null
  // - All non-completed shipments are loaded by default (controlled via id dispensing)
  // - this is only relevant for online-offline users
  if (useOnlineOffline(user) && showCompleted && !loadingOnlineShipments) {
    onLoadOnlineShipments = loadOnlineShipments
  }

  const isPsmResellerWarehouse = isPsmResellerWarehouseUser(config, user)
  const isWarehouseUser = isPsmWarehouseUser(user)
  const isDriver = isPsmDriverUser(user)

  // For drivers, include PACKED, SENT, and ARRIVED shipments (all are checkable)
  const packedDeliveries = shipments.filter(shipment => {
    if (isDriver) {
      return shipment.status === SHIPMENT_STATUS.PACKED ||
             shipment.status === SHIPMENT_STATUS.SENT ||
             shipment.status === SHIPMENT_STATUS.ARRIVED
    }
    return shipment.status === SHIPMENT_STATUS.PACKED
  })
  const packedDeliveriesIds = packedDeliveries.map(delivery => delivery.snapshotId)

  const newDeliveriesIds = shipments
    .filter(shipment => shipment.status === SHIPMENT_STATUS.NEW && shipment.routeId)
    .map(delivery => delivery.snapshotId)

  return (
    loading
      ? <div />
      : (
        <Fragment>
          {offlineError && <OfflineWarning />}
          <DriverDeliveries
            isPsmResellerWarehouse={isPsmResellerWarehouse}
            shipments={shipments}
            programs={programs}
            locationName={locationName}
            showCompleted={showCompleted}
            config={config}
            loadOnlineDeliveries={onLoadOnlineShipments}
            history={history}
            isWarehouseUser={isWarehouseUser}
            packedDeliveriesIds={packedDeliveriesIds}
            newDeliveriesIds={newDeliveriesIds}
            onToggleCheckbox={onToggleCheckbox}
            selectedDeliveriesIds={selectedDeliveriesIds}
            onPackDelivery={onPackDelivery}
            closeModal={closeModal}
            showModal={showModal}
            setShowModal={setShowModal}
            packingInProgress={packingInProgress}
          />
        </Fragment>
      )
  )
}

const withHOCs = flowRight(
  withApi,
  withUser,
  withConfig
)

export default withHOCs(PSMDeliveriesContainer)
